import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import { getHeadersForRequest } from "../../utils/functions";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import type { ChartEvent, ActiveElement } from 'chart.js';
import { useNavigate } from "react-router-dom";

ChartJS.register(ArcElement, Tooltip, Legend);

type CustomerPurchasesResponse = {
  // keys are like "123:Acme Corp" ⇒ [month1, month2, …]
  customerPurchases: { [key: string]: number[] };
};

export default function CustomerPurchasesPieChart() {
  const [chartData, setChartData] = useState<any>(null);
  const [customerList, setCustomerList] = useState<{ id: number; name: string }[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useCustomTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerPurchases = async () => {
      try {
        const headers = await getHeadersForRequest();
        const resp = await axios.get<CustomerPurchasesResponse>(
          `${process.env.REACT_APP_TEST_API_URL}/analytics/monthly/customer-purchases`,
          { headers }
        );
        const { data, customers } = processCustomerPurchases(resp.data);
        setChartData(data);
        setCustomerList(customers);
        setIsLoaded(true);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCustomerPurchases();
  }, []);

  const processCustomerPurchases = (data: CustomerPurchasesResponse) => {
    // Build up totals *and* track IDs
    const temp: { id: number; name: string; total: number }[] = [];

    Object.entries(data.customerPurchases).forEach(([key, monthly]) => {
      const [idStr, name] = key.split(":");
      const id = parseInt(idStr, 10);
      const total = monthly.reduce((sum, v) => sum + v, 0);
      temp.push({ id, name, total });
    });

    // Sort descending by total
    temp.sort((a, b) => b.total - a.total);

    // Extract chart‐friendly arrays
    const labels = temp.map((x) => x.name);
    const values = temp.map((x) => x.total);

    // And keep the id/name list for click lookup
    const customers = temp.map((x) => ({ id: x.id, name: x.name }));

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Total Purchases",
            data: values,
            backgroundColor: [
              "#FF6384",
              "#36A2EB",
              "#FFCE56",
              "#4BC0C0",
              "#9966FF",
              "#FF9F40",
            ],
          },
        ],
      },
      customers,
    };
  };

  const navigateToAnalytics = (customerId: number, customerName: string) => {
    navigate(`/CustomerAnalytics/${customerId}/${customerName}`);
  };

  if (!isLoaded) return <p>{t("Loading...")}</p>;

  return (
    <div style={{ width: "70%", margin: "0 auto" }}>
      {chartData && (
        <Doughnut
          data={chartData}
          options={{
            plugins: {
              legend: { display: false },
              tooltip: { enabled: true },
            },
            // chart.js onClick handler:
            onClick: (e: ChartEvent, elements: ActiveElement[]) => {
              if (!elements.length) return;
              const { index } = elements[0];
              const { id, name } = customerList[index];
              navigateToAnalytics(id, name);
            },
          }}
        />
      )}
    </div>
  );
}
